/**
 * One visit to the new-estimate page. Every "New estimate" shares a route, so
 * its recovery copy is keyed by a per-visit id that lives on the browser's
 * history entry: a reload or Back to that entry finds its draft again, and a
 * fresh navigation (a new entry, no id) starts blank.
 *
 * The id is written with history.replaceState directly, NOT a router navigate.
 * A router replace would change location.key, which is how the app tells a
 * first load from in-app navigation (useHasInAppHistory), and would count a
 * second page view for every new estimate.
 */
const STATE_FIELD = 'estimateDraftVisit';
const AUTH_RESUME_KEY = 'estimate-draft-visit:auth-resume:v1';

type VisitLocation = { key: string; state: unknown; pathname: string; search: string };

const visitFrom = (state: unknown): string | null => {
  const value = (state as Record<string, unknown> | null | undefined)?.[STATE_FIELD];
  return typeof value === 'string' && value ? value : null;
};

/** The history entry's own id, when the browser is on this location's entry. */
function visitOnHistoryEntry(location: VisitLocation): string | null {
  const entry = window.history.state as { key?: string; usr?: unknown } | null;
  return (entry?.key ?? 'default') === location.key ? visitFrom(entry?.usr) : null;
}

function writeVisitToHistoryEntry(location: VisitLocation, visit: string): void {
  const entry = window.history.state as { key?: string; usr?: object | null } | null;
  if ((entry?.key ?? 'default') !== location.key) return;
  window.history.replaceState({ ...entry, usr: { ...entry?.usr, [STATE_FIELD]: visit } }, '');
}

function takeAuthResume(location: VisitLocation): string | null {
  try {
    const raw = sessionStorage.getItem(AUTH_RESUME_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(AUTH_RESUME_KEY);
    const resume = JSON.parse(raw) as { path?: unknown; visit?: unknown };
    return resume.path === `${location.pathname}${location.search}` && typeof resume.visit === 'string'
      ? resume.visit
      : null;
  } catch {
    return null;
  }
}

function newVisitId(): string {
  // randomUUID is missing on older Safari and outside secure contexts.
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The visit id for this location. Idempotent per history entry, so it is safe
 * to call while rendering (the id must exist before the editor reads its draft).
 */
export function resolveEstimateDraftVisit(location: VisitLocation): string {
  const existing = visitFrom(location.state) ?? visitOnHistoryEntry(location);
  if (existing) return existing;
  const visit = takeAuthResume(location) ?? newVisitId();
  writeVisitToHistoryEntry(location, visit);
  return visit;
}

/**
 * The sign-in redirect replaces the history entry, which would strand a new
 * estimate's unsaved draft under an id nothing can reach. Remember the id once
 * for the same path, so landing back there after sign-in reopens that draft.
 */
export function rememberEstimateVisitForSignIn(location: VisitLocation): void {
  const visit = visitFrom(location.state) ?? visitOnHistoryEntry(location);
  if (!visit) return;
  try {
    sessionStorage.setItem(AUTH_RESUME_KEY, JSON.stringify({ path: `${location.pathname}${location.search}`, visit }));
  } catch {
    // Storage blocked: the draft could not have been saved either.
  }
}
